import { Box, IconButton, useTheme, Typography, Tooltip, Avatar, Divider } from "@mui/material";
import { useContext, useState, useEffect } from "react";
import { ColorModeContext, tokens } from "../../theme";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import LogoutIcon from '@mui/icons-material/Logout';
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DoctorService from "../../services/doctorService";
import LanguageSelector from "../../components/LanguageSelector";

const Topbar = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const colorMode = useContext(ColorModeContext);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('common');
  const doctorService = new DoctorService();
  const [doctorInfo, setDoctorInfo] = useState(null);

  // Get locale for date formatting based on current language
  const getDateLocale = () => {
    const currentLang = i18n.language || 'en';
    if (currentLang.startsWith('tr')) return 'tr-TR';
    if (currentLang.startsWith('fr')) return 'fr-FR';
    return 'en-US';
  };

  // Format current date based on selected language
  const formattedDate = new Date().toLocaleDateString(getDateLocale(), { 
    weekday: 'long', 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  // Doktor bilgilerini çek
  useEffect(() => {
    const fetchDoctorInfo = async () => {
      try {
        const info = await doctorService.getDoctorInfo();
        setDoctorInfo(info);
      } catch (error) {
        console.error("Error fetching doctor info:", error);
      }
    };
    fetchDoctorInfo();
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("doctorId");
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("languageManuallySet"); // Manuel dil tercihini temizle
    navigate("/individual-login");
  };
  return (
    <Box 
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 999,
        backgroundColor: colors.primary[400],
        backgroundImage: `linear-gradient(90deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`,
        borderBottom: `1px solid ${colors.grey[800]}30`,
        boxShadow: `0 4px 24px rgba(0, 0, 0, 0.1)`,
        backdropFilter: 'blur(10px)',
        mb: 2,
      }}
    >
      <Box 
        display="flex" 
        justifyContent="space-between" 
        alignItems="center"
        px={3}
        py={1.5}
      >
        {/* Welcome Section */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box>
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 700,
                background: `linear-gradient(135deg, ${colors.grey[100]} 0%, ${colors.blueAccent[300]} 100%)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              {t('welcome')}, {doctorInfo?.doctor?.name || t('doctor')}! 👋
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: colors.grey[300],
                mt: 0.5,
                fontSize: '0.85rem'
              }}
            >
              {formattedDate}
            </Typography>
          </Box>
        </Box>

        {/* Actions Section */}
        <Box 
          display="flex" 
          alignItems="center"
          gap={1}
        >
          {/* Theme Toggle */}
          <Tooltip title={theme.palette.mode === "dark" ? t('lightMode') : t('darkMode')}>
            <IconButton 
              onClick={colorMode.toggleColorMode}
              sx={{
                backgroundColor: `${colors.blueAccent[700]}30`,
                border: `1px solid ${colors.blueAccent[500]}40`,
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: `${colors.blueAccent[600]}50`,
                  transform: 'rotate(180deg)',
                }
              }}
            >
              {theme.palette.mode === "dark" ? (
                <LightModeOutlinedIcon sx={{ color: colors.blueAccent[300] }} />
              ) : (
                <DarkModeOutlinedIcon sx={{ color: colors.blueAccent[300] }} />
              )}
            </IconButton>
          </Tooltip>

          {/* Notifications */}
          <Tooltip title={t('notifications')}>
            <IconButton
              sx={{
                backgroundColor: `${colors.blueAccent[700]}30`,
                border: `1px solid ${colors.blueAccent[500]}40`,
                position: 'relative',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: `${colors.blueAccent[600]}50`,
                }
              }}
            >
              <NotificationsOutlinedIcon sx={{ color: colors.blueAccent[300] }} />
              {/* Badge for notifications */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 6,
                  right: 6,
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: colors.redAccent[500],
                  border: `2px solid ${colors.primary[400]}`,
                }}
              />
            </IconButton>
          </Tooltip>

          {/* Language Selector */}
          <LanguageSelector />

          <Divider 
            orientation="vertical" 
            flexItem 
            sx={{ 
              mx: 1, 
              borderColor: `${colors.grey[700]}50`,
              height: 'auto',
              alignSelf: 'stretch'
            }} 
          />

          {/* User Profile */}
          <Tooltip title={t('profile')}>
            <Box
              onClick={() => navigate("/profile")}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 2,
                py: 1,
                borderRadius: '12px',
                backgroundColor: `${colors.blueAccent[800]}20`,
                border: `1px solid ${colors.blueAccent[700]}40`,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: `${colors.blueAccent[700]}40`,
                  transform: 'translateY(-2px)',
                  boxShadow: `0 4px 12px ${colors.blueAccent[500]}30`,
                }
              }}
            >
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  background: `linear-gradient(135deg, ${colors.blueAccent[500]} 0%, ${colors.greenAccent[500]} 100%)`,
                  border: `2px solid ${colors.primary[400]}`,
                  fontSize: '0.9rem',
                  fontWeight: 700
                }}
              >
                {doctorInfo?.doctor?.name?.charAt(0) || 'D'}
              </Avatar>
              <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontWeight: 600,
                    color: colors.grey[100],
                    fontSize: '0.85rem'
                  }}
                >
                  {doctorInfo?.doctor?.name} {doctorInfo?.doctor?.surname}
                </Typography>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: colors.greenAccent[400],
                    fontSize: '0.7rem'
                  }}
                >
                  {doctorInfo?.doctor?.specialization || t('doctor')}
                </Typography>
              </Box>
            </Box>
          </Tooltip>

          {/* Logout */}
          <Tooltip title={t('logout')}>
            <IconButton 
              onClick={handleLogout}
              sx={{
                backgroundColor: `${colors.redAccent[700]}30`,
                border: `1px solid ${colors.redAccent[500]}40`,
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: `${colors.redAccent[600]}50`,
                  transform: 'scale(1.1)',
                }
              }}
            >
              <LogoutIcon sx={{ color: colors.redAccent[300] }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
};

export default Topbar;